import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bot, X, Send, LogIn, Sparkles, Lightbulb } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SUGGESTIONS = [
  "What is delta-v?",
  "Explain LEO",
  "Tell me about the ISS",
  "How does GTO work?",
  "What is Starship?",
  "Explain a black hole",
  "What is JWST?",
  "Apollo program",
];

/**
 * R2-A2 Mentor Droid — persistent floating chat bubble.
 * • Guest: shows login/signup prompt.
 * • Authenticated: chats via /api/droid/chat and renders bulleted sections.
 */
export default function R2A2Chat() {
  const { user, ready } = useAuth();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null); // {used, limit, remaining}
  const [messages, setMessages] = useState([
    {
      role: "droid",
      sections: [{
        heading: "R2-A2 mentor uplink",
        bullets: [
          "Ping me with a space term — LEO, delta-v, ISS, Apollo, Falcon, Starship, JWST, black hole …",
          "I return concise mentor tips plus live news headlines for context.",
          "Tap a suggestion below to get started.",
        ],
      }],
    },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, busy]);

  // Fetch quota when panel opens for an authed pilot.
  const refreshQuota = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/droid/quota");
      setQuota(r.data);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (open && user) refreshQuota();
  }, [open, user, refreshQuota]);

  const sendText = async (text) => {
    text = (text || "").trim();
    if (!text || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await api.post("/droid/chat", { message: text });
      setMessages(m => [...m, {
        role: "droid",
        sections: r.data?.sections || [],
        matched: r.data?.matched || [],
      }]);
    } catch (err) {
      setMessages(m => [...m, {
        role: "droid",
        sections: [{
          heading: "R2-A2 transmission lost",
          bullets: [err?.response?.data?.detail || "Uplink failed. Try again in a moment."],
        }],
      }]);
    } finally {
      setBusy(false);
      refreshQuota();
    }
  };

  const send = (e) => { e?.preventDefault(); sendText(input); };

  if (!ready) return null;

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="r2a2-trigger"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 sat-brackets sat-surface border border-[color:var(--sat-border-bright)] hover:border-[color:var(--sat-cyan)] p-3 pr-4 flex items-center gap-2 font-display text-[11px] tracking-[0.2em] uppercase group"
          aria-label="Open R2-A2 mentor droid"
        >
          <span className="relative flex items-center justify-center w-7 h-7 border border-[color:var(--sat-cyan)] sat-cyan">
            <Bot size={14} />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-[color:var(--sat-cyan)] animate-pulse" />
          </span>
          <span className="sat-cyan">R2-A2</span>
          <span className="hidden sm:inline text-[color:var(--sat-text-dim)]">MENTOR</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="r2a2-panel"
          className="fixed bottom-6 right-6 z-50 sat-surface sat-brackets border border-[color:var(--sat-border-bright)] flex flex-col w-[min(92vw,380px)] max-h-[min(80vh,560px)]"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[color:var(--sat-border)] px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="relative flex items-center justify-center w-6 h-6 border border-[color:var(--sat-cyan)] sat-cyan">
                <Bot size={12} />
              </span>
              <div className="leading-tight">
                <div className="sat-label sat-cyan">R2-A2 MENTOR DROID</div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-[color:var(--sat-text-dim)]">
                  {user ? `Uplink · ${user.email}` : "Guest · uplink locked"}
                </div>
              </div>
            </div>
            <button
              data-testid="r2a2-close"
              onClick={() => setOpen(false)}
              className="p-1.5 border border-[color:var(--sat-border)] text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)] hover:border-[color:var(--sat-cyan)]"
              aria-label="Close R2-A2"
            >
              <X size={12} />
            </button>
          </div>

          {/* Body */}
          {!user ? (
            <div className="p-5 flex-1 flex flex-col items-start gap-4" data-testid="r2a2-guest">
              <Sparkles size={18} className="sat-cyan" />
              <div className="font-display font-bold text-lg leading-snug">
                Uplink R2-A2 to unlock<br />mentor telemetry.
              </div>
              <p className="text-sm text-[color:var(--sat-text-dim)]">
                Sign in or create a pilot ID to chat with R2-A2 for orbital primers, mission context and live news headlines.
              </p>
              <Link
                to="/auth"
                data-testid="r2a2-login-link"
                onClick={() => setOpen(false)}
                className="sat-btn sat-btn-solid !py-2 !px-4 inline-flex items-center gap-2"
              >
                <LogIn size={12} /> LOG IN / REGISTER
              </Link>
            </div>
          ) : (
            <>
              <div
                ref={scrollRef}
                data-testid="r2a2-log"
                className="flex-1 overflow-y-auto px-3 py-3 space-y-3 text-sm"
              >
                {messages.map((m, i) =>
                  m.role === "user" ? (
                    <div key={i} className="flex justify-end" data-testid="r2a2-user-msg">
                      <div className="max-w-[85%] border border-[color:var(--sat-cyan)] sat-cyan px-2.5 py-1.5 font-mono text-[12px]">
                        {m.text}
                      </div>
                    </div>
                  ) : (
                    <div key={i} className="space-y-2" data-testid="r2a2-droid-msg">
                      {(m.sections || []).map((s, j) => (
                        <div key={j} className="border border-[color:var(--sat-border)] bg-black/40 p-2.5">
                          <div className="sat-label sat-cyan mb-1.5">{s.heading}</div>
                          {s.definition && (
                            <p
                              data-testid="r2a2-definition"
                              className="text-[12px] leading-relaxed text-[color:var(--sat-text)] mb-2 pb-2 border-b border-[color:var(--sat-border)]"
                            >
                              {s.definition}
                            </p>
                          )}
                          <ul className="space-y-1 font-mono text-[12px] text-[color:var(--sat-text)]">
                            {(s.bullets || []).map((b, k) => (
                              <li key={k} className="flex gap-1.5 leading-snug">
                                <span className="sat-cyan flex-shrink-0">›</span><span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )
                )}
                {busy && (
                  <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid="r2a2-busy">
                    R2-A2 · PROCESSING…
                  </div>
                )}
              </div>

              {/* Suggested questions */}
              <div className="border-t border-[color:var(--sat-border)] px-2 py-2" data-testid="r2a2-suggestions">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Lightbulb size={10} className="sat-cyan" />
                  <div className="sat-label sat-cyan text-[9px]">SUGGESTED</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`r2a2-suggestion-${i}`}
                      onClick={() => sendText(s)}
                      disabled={busy}
                      className="border border-[color:var(--sat-border)] hover:border-[color:var(--sat-cyan)] hover:sat-cyan px-2 py-1 font-mono text-[10px] text-[color:var(--sat-text-dim)] disabled:opacity-40 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={send} className="border-t border-[color:var(--sat-border)] p-2 flex gap-2 items-center">
                <input
                  data-testid="r2a2-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about a space term…"
                  className="flex-1 bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-2 py-2 font-mono text-[12px] text-[color:var(--sat-text)]"
                  maxLength={400}
                />
                <button
                  type="submit"
                  data-testid="r2a2-send"
                  disabled={busy || !input.trim()}
                  className="sat-btn sat-btn-solid !py-2 !px-3 disabled:opacity-40"
                >
                  <Send size={12} />
                </button>
              </form>
              {quota && (
                <div
                  data-testid="r2a2-quota"
                  className="px-3 pb-2 pt-0 text-[9px] tracking-[0.15em] uppercase text-[color:var(--sat-text-dim)] flex justify-between"
                >
                  <span>DAILY QUOTA</span>
                  <span className={quota.remaining <= 5 ? "text-[color:var(--sat-danger)]" : "sat-cyan"}>
                    {quota.used}/{quota.limit} · {quota.remaining} LEFT
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
